import React from "react"
import type { TableModel } from "utils/tableModel"
import {
  DataTile,
  Tile,
  isDataTile,
} from "features/Dashboard/types/dashboardDocument"
import { TileRuntime } from "features/Dashboard/runtime/dashboardStore"
import { useTileView } from "features/Dashboard/runtime/tileView"
import { canRepeat } from "features/Dashboard/runtime/panels"
import TableTile from "./TableTile.component"
import TextTile from "./TextTile.component"
import ImageTile from "./ImageTile.component"
import BarTile from "./BarTile.component"
import LineTile from "./LineTile.component"
import PieTile from "./PieTile.component"
import KpiTile from "./KpiTile.component"
import ScorecardTile from "./ScorecardTile.component"
import PanelGrid from "./PanelGrid.component"

/**
 * A data tile drawing its view: its last result with its measures and
 * category display applied (see `useTileView`). A chart also gets the last
 * result itself, `raw`, for its colours, and a KPI and a scorecard for
 * their base.
 */
const DataTileBody: React.FC<{ tile: DataTile; raw: TableModel }> = ({
  tile,
  raw,
}) => {
  const model = useTileView(tile)
  if (!model) return null
  switch (tile.type) {
    case "table":
      return <TableTile tile={tile} model={model} />
    case "bar":
      return <BarTile tile={tile} model={model} raw={raw} />
    case "line":
      return <LineTile tile={tile} model={model} raw={raw} />
    case "pie":
      return <PieTile tile={tile} model={model} raw={raw} />
    case "kpi":
      return <KpiTile tile={tile} model={model} rawModel={raw} />
    case "scorecard":
      return <ScorecardTile tile={tile} model={model} rawModel={raw} />
  }
}

/**
 * The tile's content by type; a data tile draws nothing until it has a
 * model, and shows its runtime message (a prompt such as "Drop a variable
 * here", or an output that is not a table) as text whatever its type. A
 * repeated tile draws its panels.
 */
const TileBody: React.FC<{ tile: Tile; runtime?: TileRuntime }> = ({
  tile,
  runtime,
}) => {
  if (!isDataTile(tile)) {
    return tile.type === "text" ? (
      <TextTile tile={tile} />
    ) : (
      <ImageTile tile={tile} />
    )
  }
  if (runtime?.message) {
    return <p className="dq-tile__empty dq-tile__message">{runtime.message}</p>
  }
  if (runtime?.panels && canRepeat(tile)) {
    return <PanelGrid tile={tile} panels={runtime.panels} />
  }
  return runtime?.model ? (
    <DataTileBody tile={tile} raw={runtime.model} />
  ) : null
}

export default TileBody
